import { useEffect, useMemo } from 'react';
import {
  dispatch,
  getSearchParameter,
  updateShowSearchWindow,
  useAppSelector,
  useGetProductsMutation,
  useLazyGetCustomerListSearchQuery,
  useLazyGetTeamSearchQuery,
  useListOrdersMutation,
} from '@zonofi/zono-money-store';
import {
  useDebounceSearch,
  useSearchListApi,
  useUrlDateRange,
} from '../../hooks';

export const useOrderSearch = () => {
  const { debouncedSearchValue } = useDebounceSearch();
  const searchParameter = useAppSelector(getSearchParameter);

  const { dateRangeFromUrl: appliedDateRange } = useUrlDateRange();

  const [getCustomerSearchApi, customerSearchApiResponse] =
    useLazyGetCustomerListSearchQuery();
  const [fetchOrderSearchApi, orderSearchApiResponse] = useListOrdersMutation();
  const [getTeamMemberSearchApi, teamMemberSearchApiResponse] =
    useLazyGetTeamSearchQuery();
  const [fetchProductSearchApi, productSearchApiResponse] =
    useGetProductsMutation();

  const { fetchSearchListApi } = useSearchListApi({
    fetchOrderSearchApi,
    fetchCustomerSearchApi: getCustomerSearchApi,
    fetchTeamMemberSearchApi: getTeamMemberSearchApi,
    fetchProductSearchApi,
  });

  const searchResult = useMemo(() => {
    if (!searchParameter?.parameterId || !searchParameter?.value) {
      return [];
    }

    switch (searchParameter.parameterId) {
      case 'customerId':
        return customerSearchApiResponse?.data?.customers ?? [];
      case 'userId':
        return teamMemberSearchApiResponse?.data?.data ?? [];
      case 'searchKeyword':
        return orderSearchApiResponse?.data?.order ?? [];
      case 'productVariantIds':
        return productSearchApiResponse?.data?.products ?? [];
      default:
        return [];
    }
  }, [
    searchParameter?.parameterId,
    searchParameter?.value,
    customerSearchApiResponse,
    teamMemberSearchApiResponse,
    orderSearchApiResponse,
    productSearchApiResponse,
  ]);

  const isSearchListApiLoading = useMemo(() => {
    return (
      customerSearchApiResponse?.isLoading ||
      teamMemberSearchApiResponse?.isLoading ||
      productSearchApiResponse?.isLoading
    );
  }, [
    customerSearchApiResponse,
    teamMemberSearchApiResponse,
    productSearchApiResponse,
  ]);

  const isOrderSearchApiLoading = useMemo(() => {
    return orderSearchApiResponse?.isLoading;
  }, [orderSearchApiResponse]);

  useEffect(() => {
    if (
      searchParameter &&
      searchParameter.parameterId?.length !== 0 &&
      searchParameter.value?.length !== 0
    ) {
      try {
        fetchSearchListApi({
          paramId: searchParameter?.parameterId || '',
          value: debouncedSearchValue || '',
          appliedDateRange: appliedDateRange,
        });
      } catch (e) {
        console.error('Error fetching search list: ', e);
      }
    }
  }, [debouncedSearchValue]);

  useEffect(() => {
    if (searchResult && debouncedSearchValue?.length > 0) {
      dispatch(updateShowSearchWindow(true));
    }
  }, [searchResult, debouncedSearchValue]);

  return {
    isSearchListApiLoading,
    searchResult,
    isOrderSearchApiLoading,
  };
};
